import type { RefObject } from 'react'
import { useCallback, useContext, useEffect, useMemo } from 'react'

import type {
  AccommodationDayPickerSize,
  AccommodationMobileDayCellSelectHandler,
} from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendar.types'
import AccommodationCalendarDayPicker from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarDayPicker'
import type { AccommodationDayCellVariant } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarLargeDayCell'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import type { FeaturedDateRange } from 'lib/datetime/calendarDayViews'
import { generateStaticDayViews } from 'lib/datetime/calendarDayViews'
import noop from 'lib/function/noop'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'
import { getCalendarDayPrices } from 'selectors/offerPage/propertyOfferPageSelectors'

interface Props {
  /** Destination time zone. Determines earliest permissible check-in date. */
  propertyTimezone?: string
  featuredDateRanges?: Utils.NonEmptyArray<FeaturedDateRange> | undefined
  dayPrices?: { [isoDate: string]: App.Property.CalendarDay } | undefined
  displayLuxPlusPrice: boolean
  checkInDate?: string
  checkOutDate?: string
  /**
   * Reference to the scroll container element this picker sits in
   * This is necessary to provide so it can manage showing the correct month
   */
  scrollerRef: RefObject<HTMLElement | null>
  size: AccommodationDayPickerSize
  /**
   * The month we're picking the date for inso ISO date format
   */
  selectedMonth?: string
  calendarStartDate?: string

  /**
   * Event for when the selected dates change
   */
  onDatesChange?: AccommodationMobileDayCellSelectHandler

  availabilityVariantMap?: Map<string, AccommodationDayCellVariant>
  renderFromStart?: boolean
}

/**
 * Targets the promotion, but keeps customers on their own check-in month once it already
 * falls on or after the target, so they are never sent back to an earlier month.
 */
function resolvePromotionIsoDate(checkInIsoDate: string | undefined, targetIsoDate: string | undefined) {
  if (!targetIsoDate) return undefined
  return (checkInIsoDate && checkInIsoDate >= targetIsoDate) ? checkInIsoDate : targetIsoDate
}

export function OfferDatePicker({
  propertyTimezone,
  featuredDateRanges,
  dayPrices,
  displayLuxPlusPrice,
  checkInDate,
  scrollerRef,
  checkOutDate,
  size,
  selectedMonth,
  calendarStartDate,
  onDatesChange = noop,
  availabilityVariantMap,
  renderFromStart,
}: Props) {
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const onDatesSelect = useCallback<AccommodationMobileDayCellSelectHandler>(day => {
    onDatesChange(day)
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.DateChanged)
  }, [onDatesChange, onUserAnalyticsAction])

  const staticDayViews = useMemo(() => generateStaticDayViews(propertyTimezone, featuredDateRanges, dayPrices, displayLuxPlusPrice), [propertyTimezone, featuredDateRanges, dayPrices, displayLuxPlusPrice])
  const firstFeaturedIsoDate = staticDayViews.find(day => day.featured)?.date
  const initialDate = selectedMonth ?? resolvePromotionIsoDate(checkInDate, calendarStartDate ?? firstFeaturedIsoDate)
  const month = useMemo(() => {
    if (!initialDate) return undefined
    // Match a rendered day so past promotion starts resolve to the current month.
    const day = staticDayViews.find(view => view.date >= initialDate)
    return day ? new Date(day.year, day.month, 1) : undefined
  }, [initialDate, staticDayViews])

  useEffect(() => {
    if (!month) {
      scrollerRef.current?.scrollTo({ behavior: 'auto', top: 0 })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [month])

  return <AccommodationCalendarDayPicker
    key={month ? `${month.getFullYear()}-${month.getMonth()}` : 'default'}
    startDate={checkInDate}
    endDate={checkOutDate}
    onDaySelect={onDatesSelect}
    contentRef={scrollerRef}
    views={staticDayViews}
    initialMonth={month?.getMonth()}
    initialYear={month?.getFullYear()}
    size={size}
    availabilityVariantMap={availabilityVariantMap}
    renderFromStart={renderFromStart}
  />
}

type MappedProps = Pick<Props, 'dayPrices' | 'displayLuxPlusPrice' | 'calendarStartDate'> & { open: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  calendarStartDate: state.activeFilter === 'checkin' ? state.calendarStartDate : undefined,
  dayPrices: getCalendarDayPrices(state, appState),
  displayLuxPlusPrice: !!state.displayMemberPrice,
  open: !!state.activeFilter || state.activeHeaderFilter === 'checkin',
}), OfferDatePicker, preventRenderWithClosedFilter)
